import React from "react";
import PropTypes from "prop-types";

import {
  useTable,
  useSortBy,
  useGlobalFilter,
  useFilters,
  useResizeColumns,
  useFlexLayout,
  useExpanded,
  usePagination
} from "react-table";
import { Table, InputGroup, FormControl, Row, Col, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight
} from "@fortawesome/free-solid-svg-icons";
import { matchSorter } from "match-sorter";

import GlobalFilter from "./GlobalFilter";
import { DefaultColumnFilter } from "./Filters";
// import "./DtsReactTable.css";
import "bootstrap/dist/css/bootstrap.min.css";

/** References:
 * ://react-table.tanstack.com/
 * https://blog.logrocket.com/building-styling-tables-react-table-v7/
 *
 * A Good Tutorial Series is also in YouTube:
 * https://www.youtube.com/playlist?list=PLC3y8-rFHvwgWTSrDiwmUsl4ZvipOw9Cz
 */

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

const DtsReactTable = ({
  columns: userColumns,
  data,
  cypressid,
  renderRowSubComponent,
  rowOnClick,
  rowClickHandler,
  headerColor,
  showFooter,
  showPagination,
  showGlobalFilter,
  subTableId, //used for cypress testing
  expandRows,
  expandedRowObj
}) => {
  const filterTypes = React.useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,

      includes: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? toString(rowValue).toLowerCase().includes(toString(filterValue).toLowerCase())
            : true;
        });
      },

      startsWith: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true;
        });
      }
    }),
    []
  );

  /*const sortTypes = React.useMemo(
    () => ({
      dateSort: (a, b) => {
        a = new Date(a).getTime();
        b = new Date(b).getTime();
        return b > a ? 1 : -1;
      }
    }),
    []
  );*/

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      disableFilters: true,
      minWidth: 30,
      width: 150,
      maxWidth: 500
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { globalFilter, pageIndex, pageSize }
  } = useTable(
    {
      columns: userColumns,
      data,
      initialState: {
        expanded: expandRows ? expandedRowObj : false,
        pageSize: showPagination ? 10 : data.length
      },
      defaultColumn,
      filterTypes
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    useExpanded,
    usePagination,
    useFlexLayout
  );

  return (
    <>
      <Row className='float-right'>
        <Col>{showGlobalFilter ? <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} /> : ""}</Col>
      </Row>
      <Row>
        <Col>
          <Table striped bordered hover size='sm' responsive {...getTableProps()} cypressid={cypressid}>
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <React.Fragment key={headerGroup.headers.length + "_hfrag"} className='table-header'>
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        className={`p-2 table-header ${headerColor ? "primary-" + headerColor : "primary-deq"}`}
                        {...column.getHeaderProps()}
                      >
                        <span {...column.getSortByToggleProps()}>
                          {column.render("Header")}
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <FontAwesomeIcon className='ms-3' icon={faArrowDown} />
                            ) : (
                              <FontAwesomeIcon className='ms-3' icon={faArrowUp} />
                            )
                          ) : (
                            ""
                          )}
                        </span>
                        <div {...column.getResizerProps()} className='resizer' />
                        {column.canResize && (
                          <div
                            {...column.getResizerProps()}
                            className={`resizer ${column.isResizing ? "isResizing" : ""}`}
                          />
                        )}
                        <div>{column.canFilter ? column.render("Filter") : null}</div>
                      </th>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <React.Fragment key={i + "_frag"}>
                    <tr {...row.getRowProps()} onClick={rowOnClick ? () => row.original : () => ""}>
                      {row.cells.map((cell) => {
                        return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                      })}
                    </tr>
                    {row.isExpanded ? (
                      <tr>
                        <td cypressid={row.index + subTableId}>
                          <span className='subTable'>{renderRowSubComponent({ row })}</span>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
            {showFooter ? (
              <tfoot className='fw-bold'>
                {footerGroups.map((group) => (
                  <tr {...group.getFooterGroupProps()}>
                    {group.headers.map((column) => (
                      <td {...column.getFooterProps()}>{column.render("Footer")}</td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            ) : (
              ""
            )}
          </Table>
          {showPagination ? (
            <Row className='mt-2 text-center'>
              <Col>
                <Button
                  className='me-2'
                  size='sm'
                  variant='secondary'
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                >
                  <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </Button>
                <Button
                  className='me-2'
                  size='sm'
                  variant='secondary'
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </Button>
              </Col>
              <Col>
                <span>
                  Page
                  <strong>
                    {pageIndex + 1} of {pageOptions.length}
                  </strong>
                </span>
                <span>
                  | Go to page:
                  <InputGroup size='sm' style={{ width: "20%", display: "inline-flex" }}>
                    <FormControl
                      type='number'
                      defaultValue={pageIndex + 1}
                      onChange={(e) => {
                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                        gotoPage(page);
                      }}
                    />
                  </InputGroup>
                </span>
                <InputGroup size='sm' style={{ width: "30%", display: "inline-flex" }}>
                  <FormControl
                    className='mt-4'
                    size='sm'
                    as='select'
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                    ))}
                  </FormControl>
                </InputGroup>
              </Col>
              <Col>
                <Button
                  className='me-2'
                  size='sm'
                  variant='secondary'
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </Button>
                <Button
                  className='me-2'
                  size='sm'
                  variant='secondary'
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                >
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                </Button>
              </Col>
            </Row>
          ) : (
            ""
          )}
        </Col>
      </Row>
    </>
  );
};

DtsReactTable.defaultProps = {
  rowOnClick: false,
  showFooter: false,
  showPagination: false,
  expandRows: false,
  expandedRowObj: {}
};

DtsReactTable.propTypes = {
  /**Show Footer */
  showFooter: PropTypes.bool.isRequired,

  /** Specified if pagination should show or not */
  showPagination: PropTypes.bool.isRequired,

  /** Specifies if there should be a row onClick action*/
  rowOnClick: PropTypes.bool.isRequired,

  /** OPTIONAL: The onClick Action to be taken */
  rowClickHandler: PropTypes.func,

  /** header color background. There are six possible choices. Refer to ReadMe file for specifics */
  headerColor: PropTypes.string,

  subTableId: PropTypes.string
};

export default DtsReactTable;
